import { Classification } from "@/components/Classification";
import { LoadingButton } from "@/components/LoadingButton";
import { TeamName } from "@/components/TeamName";
import { useSearchProject } from "@/features/projects/SearchProject";
import { CardTableLoader } from "@/features/workspace/widgets/Components";
import { currencyFormatter } from "@/utils/currencyFormatter";
import styled from "@emotion/styled";
import {
  type OpportunitySearch,
  type WidgetSize,
  getOpportunitiesSearchKeyCreator,
  updateProject,
} from "@repo/api";
import {
  Table as SkapaTable,
  TableBody,
  Text,
  Variables,
  pinFilledIcon,
  pinIcon,
} from "@repo/skapa";
import { T, getLanguageLocal, t } from "@repo/translation";
import {
  CoWorkerName,
  LocalizedDateAndTime,
  ProjectTitle,
  getCustomerName,
  translateOpportunityType,
  translateProjectStatus,
  useSettings,
  useToast,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";
import { unstable_serialize } from "swr/infinite";

const CompactTable = styled(SkapaTable)`
  && tbody td {
    padding-top: ${Variables.space50};
    padding-bottom: ${Variables.space50};
  }
  && tbody td:nth-child(1),
  && tbody td:nth-child(2) {
    padding-inline-end: ${Variables.space75};
    width: 1%;
    white-space: nowrap;
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;
`;

const SecondRow = styled.div`
  color: ${Variables.colourTextAndIcon2};
`;

const ProjectRow = ({
  project,
  size,
}: {
  project: OpportunitySearch;
  size: WidgetSize;
}) => {
  const navigate = useNavigate();
  const { updateToast } = useToast();
  const { market, language, general, customerMode } = useSettings();
  const { options, projectFilters } = useSearchProject();

  return (
    <ClickableRow
      onClick={() => {
        navigate(`/opportunity/${project.opportunityId}/`);
      }}
    >
      <td>
        <LoadingButton
          text={t("label.pin_project")}
          aria-pressed={project.isPinned ? "true" : "false"}
          size="small"
          iconOnly
          ssrIcon={project.isPinned ? pinFilledIcon : pinIcon}
          type="tertiary"
          onClick={() => {
            trackAnalytics("pin_project");

            updateProject(
              { id: project.opportunityId, isPinned: !project.isPinned },
              updateToast
            ).then(() => {
              mutate(
                unstable_serialize(
                  getOpportunitiesSearchKeyCreator(
                    projectFilters.filters,
                    options
                  )
                )
              );
            });
          }}
        />
      </td>
      <td>
        <Classification
          id={project.opportunityId.toString()}
          classification={project.classification}
        />
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {getCustomerName(
            {
              firstName: project.customerFirstName,
              lastName: project.customerLastName,
              organizationName: project.organizationName,
              customerType: project.customerType,
            },
            general?.localization?.reverse_names,
            customerMode
          )}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            <ProjectTitle
              title={project.title}
              customerName={project.customerName}
              organizationName={project.organizationName}
            />
          </Text>
        </SecondRow>
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {translateProjectStatus(project.status)}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            <T translationKey="label.updated" />
            {": "}
            <LocalizedDateAndTime date={project.updatedAt ?? ""} />
          </Text>
        </SecondRow>
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {currencyFormatter({
            value: project.amount,
            currency: project.currency,
            locale: getLanguageLocal(language, market),
            showSign: true,
            disableDecimals: Boolean(general?.localization?.disable_decimals),
            fractions: 2,
          })}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            {translateOpportunityType(project.opportunityType)}
          </Text>
        </SecondRow>
      </td>
      {size === "L" ? (
        <td>
          {project.assignedTo ? (
            <CoWorkerName email={project.assignedTo} textStyle="Heading.XS" />
          ) : (
            <TeamName teamId={project.assignedTeam} textStyle="Heading.XS" />
          )}
          <SecondRow>
            <Text textStyle="Body.S">{project.customerMeetingPointName}</Text>
          </SecondRow>
        </td>
      ) : null}
    </ClickableRow>
  );
};

export const Table = ({
  isLoading,
  projects,
  size,
}: {
  isLoading: boolean;
  projects: OpportunitySearch[];
  size: WidgetSize;
}) => {
  if (isLoading) {
    return <CardTableLoader />;
  }

  return (
    <CompactTable fullWidth aria-label={t("label.projects")}>
      <TableBody striped>
        {projects.map((project) => (
          <ProjectRow
            key={project.opportunityId}
            project={project}
            size={size}
          />
        ))}
      </TableBody>
    </CompactTable>
  );
};
